import { HttpStatusCode, isAxiosError } from 'axios';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isDesktop } from 'react-device-detect';
import TagManager from 'react-gtm-module';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  BaseModal,
  BaseTooltip,
  DefaultDescriptionBenefitEducation,
  type IRequestAbortController,
  formatUnknownErrorMessage,
  generateAbortRequest,
  isAbortError,
  useToast,
} from '@gbs-monorepo-packages/common';
import { colors } from '@gbs-monorepo-packages/styles';

import {
  wrapperClass,
  wrapperCss,
} from '../../components/GrapesJS/Customs/ToggleWrapper';
import { SearchCourseModal } from '../../components/SearchCourseModal';
import {
  GOOGLE_FONTS_API_URL,
  GOOGLE_TAG_MANAGER,
  IS_PROD,
} from '../../constants/Env';
import { getCourseViewLanguages } from '../../constants/Languages';
import { EdgeStorageKey } from '../../constants/Storage';
import { type IApiThrowsError } from '../../services/api';
import {
  type ICoursePageDTO,
  type ICoursePageListDTO,
} from '../../services/coursePages';
import { type IFontDTO } from '../../services/courses';
import {
  type IViewCourseDataDTO,
  getViewCourseData,
  getViewCoursePagesData,
  getViewCoursePagesList,
  postViewCourseData,
  postViewCoursePagesData,
  postViewCoursePagesList,
} from '../../services/viewCourses';
import { generateCssFromGlobalStyle } from '../../utils/generate';
import { getStorage, setStorage } from '../../utils/localStorage';
import { ByPageNumber } from '../../utils/sortObjects';
import {
  ActionIconContainer,
  ActionItemsContainer,
  AsideContainer,
  ContainerArrows,
  CourseViewerContainer,
  CustomSelectData,
  Description,
  ErrorMessage,
  ExitFullScreenMobile,
  ExitFullscreenIcon,
  Fieldset,
  Footer,
  Form,
  FullscreenIcon,
  FullscreenMessageContainer,
  Header,
  HeaderImage,
  HeaderImageContainer,
  HiddenMobileLayoutIcon,
  IframeCustom,
  Input,
  Label,
  LeftArrowIcon,
  LeftArrowIconContainer,
  LeftArrowIconFullScreen,
  LoadingPages,
  LoadingPagesContainer,
  LockButton,
  LockedIcon,
  MainContainer,
  MainContent,
  MobileIframe,
  NoPagesContainer,
  NoPagesText,
  OverlayToggleToMobile,
  PageContainer,
  PageIcon,
  PageIconContainer,
  PageItem,
  PageItemsContainer,
  PageMessage,
  PageTitle,
  PageTitleContainer,
  PagesMenuIcon,
  PrimaryContent,
  RightArrowIcon,
  RightArrowIconContainer,
  RightArrowIconFullScreen,
  SearchIcon,
  SecondaryContent,
  SubmitButton,
  TextContent,
  Title,
  ToggleFullscreenButton,
  ToggleLeftButton,
  ToggleRightButton,
  UnLockedIcon,
  VisibleMobileLayoutIcon,
} from './styles';

interface IPasscodeError {
  passcode?: string;
  other?: string;
}

const generateIframeDoc = ({
  fontsImportString,
  cssGlobalStyles,
  currentPage,
  wrapperClass,
  wrapperCss,
  viewport = 'width=device-width, initial-scale=1.0',
}: {
  fontsImportString: string;
  cssGlobalStyles: string;
  currentPage: ICoursePageDTO | null;
  wrapperClass: string;
  wrapperCss: string;
  viewport?: string;
}) => {
  return `
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="${viewport}">
    <meta name="x-apple-disable-message-reformatting">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <style type="text/css">${wrapperCss}</style>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    ${fontsImportString}
    <style type="text/css">${currentPage?.cssContent ?? ''}</style>
    <style type="text/css">${cssGlobalStyles}</style>
    <table id="gjs_body" style="table-layout: fixed;border-spacing: 0;mso-table-lspace: 0pt;mso-table-rspace: 0pt;vertical-align: top;min-width: 320px;Margin: 0 auto;background-color: transparent;width:100%" cellpadding="0" cellspacing="0">
      <tbody>
        <tr style="vertical-align: top">
          <td ${
            (currentPage?.hasWrapper ?? false) ? `class="${wrapperClass}"` : ''
          } style="vertical-align: top;">
            ${currentPage?.htmlContent ?? ''}
          </td>
        </tr>
      </tbody>
    </table>
    <script>
      document.addEventListener('keydown', function(event) {
        window.parent.postMessage({
          key: event.key,
          clientX: null
        }, '*');
      });
      (function () {
        let ticking = false;
        document.addEventListener('mousemove', function (event) {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            window.parent.postMessage(
              { key: null, clientX: event.clientX },
              '*'
            );
            ticking = false;
          });
        });
      })();
    </script>
  `;
};

export const CourseViewer = (): JSX.Element => {
  const desktopIframeRef = useRef<HTMLIFrameElement | null>(null);
  const mobileIframeRef = useRef<HTMLIFrameElement | null>(null);

  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [isSearchModalOpen, setSearchModalOpen] = useState<boolean>(false);
  const [showPasscodeModal, setShowPasscodeModal] = useState<boolean>(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [showPagesContainer, setShowPagesContainer] = useState<boolean>(false);
  const [isPagesMenuOpen, setPagesMenuOpen] = useState<boolean>(false);
  const [showFullscreenMessageContainer, setShowFullscreenMessageContainer] =
    useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [loadingPasscodeSubmit, setLoadingPasscodeSubmit] =
    useState<boolean>(false);
  const [course, setCourse] = useState<IViewCourseDataDTO | null>(null);
  const [sortedPages, setSortedPages] = useState<ICoursePageListDTO[]>([]);
  const [pagesData, setPagesData] = useState<ICoursePageDTO[]>([]);
  const [currentPage, setCurrentPage] = useState<ICoursePageDTO | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [passcode, setPasscode] = useState<string>('');
  const [error, setError] = useState<IPasscodeError>({});
  const [fontsImportString, setFontsImportString] = useState<string>('');
  const { viewURL = '' } = useParams();
  const headerImagePath = course?.courseFile?.path ?? '';

  const [showButtonLeft, setShowButtonLeft] = useState(false);
  const [showButtonRight, setShowButtonRight] = useState(false);
  const [language, setLanguage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const [isSplitView, setIsSplitView] = useState<boolean>(false);
  const abortControllerRef = useRef<IRequestAbortController | null>(null);

  const allPagesLoaded = sortedPages.length === pagesData.length;

  const availableLanguages = useMemo(
    () => getCourseViewLanguages(course?.clientId),
    [course?.clientId]
  );

  const fetchCoursePagesList = useCallback(async () => {
    try {
      setIsLoadingData(true);
      let pagesList;
      const pass = getStorage<string>(EdgeStorageKey.VIEWCOURSE_PASSCODE);
      if (course?.passcodeProtected === true) {
        pagesList = await postViewCoursePagesList({
          viewURL,
          passcode: pass || '',
        });
      } else {
        pagesList = await getViewCoursePagesList({
          viewURL,
        });
      }

      const courseSortedPages = pagesList.sort(ByPageNumber);

      setSortedPages(courseSortedPages);
    } catch (error) {
      addToast({
        title: 'Error loading course pages',
        description: formatUnknownErrorMessage(
          error,
          DefaultDescriptionBenefitEducation
        ),
        styleType: 'error',
      });
    } finally {
      setIsLoadingData(false);
    }
  }, [course?.passcodeProtected, viewURL, addToast]);

  const makePageIdsByPageNumber = useCallback(
    (pageNumber: number) => {
      const page = pageNumber !== 0 ? pageNumber : 1;

      const totalPages = sortedPages.length;
      const backward = 2;
      const forward = 2;
      const min = page - backward > 0 ? page - backward : 1;
      const max = page + forward < totalPages ? page + forward : totalPages;

      const pagesToFind: ICoursePageListDTO[] = [];
      for (let i = min; i <= max; i++) {
        pagesToFind.push(sortedPages[i - 1]);
      }

      const pageIds: number[] = [];
      pagesToFind.forEach((value) => {
        const pageFound = pagesData.find((page) => page.id === value.id);
        if (!pageFound) {
          pageIds.push(value.id);
        }
      });

      return pageIds.join(',');
    },
    [pagesData, sortedPages]
  );

  const fetchCoursePagesData = useCallback(async () => {
    let pageParam = Number(searchParams.get('page'));
    if (pageParam > sortedPages.length) {
      pageParam = 0;
      searchParams.set('page', '1');
      setSearchParams(searchParams);
    }
    const index = pageParam !== 0 ? pageParam - 1 : 0;
    const sortedPage = sortedPages[index];

    const pageLoaded = pagesData.find((item) => item.id === sortedPage.id);
    if (pageLoaded) {
      setCurrentPage(pageLoaded);
      return;
    }

    const pageIds = makePageIdsByPageNumber(pageParam);
    if (!pageIds) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    try {
      setIsLoadingData(true);
      const pass = getStorage<string>(EdgeStorageKey.VIEWCOURSE_PASSCODE);
      let pages;
      if (course?.passcodeProtected === true) {
        pages = await postViewCoursePagesData({
          viewURL,
          pageIds,
          passcode: pass || '',
          language: language !== 'en' ? language : undefined,
        });
      } else {
        pages = await getViewCoursePagesData({
          viewURL,
          pageIds,
          language: language !== 'en' ? language : undefined,
        });
      }

      if (pageParam) {
        const pageItem = pages.find((item) => item.id === sortedPage.id);
        if (pageItem) {
          setCurrentPage(pageItem);
          setCurrentPageIndex(pageItem.pageNumber - 1);
        }
      } else if (currentPage === null) {
        setCurrentPage(pages[0]);
      } else {
        const selectedPage = pages.find((page) => page.id === currentPage.id);
        if (selectedPage) {
          setCurrentPage(selectedPage);
        }
      }

      setPagesData((prevPages) => {
        const existingIds = new Set(prevPages.map((p) => p.id));
        const newPages = pages.filter((page) => !existingIds.has(page.id));
        return [...prevPages, ...newPages];
      });
    } catch (error) {
      if (!isAbortError(error)) {
        addToast({
          title: 'Error loading course pages data',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
        });
      }
    } finally {
      setIsLoadingData(false);
    }
  }, [
    searchParams,
    sortedPages,
    pagesData,
    makePageIdsByPageNumber,
    course?.passcodeProtected,
    currentPage,
    viewURL,
    language,
    addToast,
  ]);

  const fetchNextPages = useCallback(async () => {
    if (allPagesLoaded || abortControllerRef.current) {
      return;
    }

    const pageIdsToFetch: number[] = [];
    sortedPages.forEach((item) => {
      const pageLoaded = pagesData.find((page) => page.id === item.id);
      if (!pageLoaded) {
        pageIdsToFetch.push(item.id);
      }
    });

    if (pageIdsToFetch.length === 0) {
      return;
    }

    const pagesToFetch = pageIdsToFetch.slice(0, 3);
    const pageIds = pagesToFetch.join(',');

    const controller = generateAbortRequest(true);
    abortControllerRef.current = controller;

    try {
      const pass = getStorage<string>(EdgeStorageKey.VIEWCOURSE_PASSCODE);
      let newPages: ICoursePageDTO[] = [];

      if (course?.passcodeProtected === true) {
        newPages = await postViewCoursePagesData({
          viewURL,
          pageIds,
          passcode: pass || '',
          language: language !== 'en' ? language : undefined,
          signal: controller.signal,
        });
      } else {
        newPages = await getViewCoursePagesData({
          viewURL,
          pageIds,
          language: language !== 'en' ? language : undefined,
          signal: controller.signal,
        });
      }

      if (newPages.length) {
        setPagesData((prevPages) => {
          const existingIds = new Set(prevPages.map((p) => p.id));
          const uniqueNewPages = newPages.filter(
            (page) => !existingIds.has(page.id)
          );
          return [...prevPages, ...uniqueNewPages];
        });
      }
    } catch (error) {
      // Silent fail for background loading (including aborted requests)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [
    sortedPages,
    pagesData,
    viewURL,
    language,
    course?.passcodeProtected,
    allPagesLoaded,
  ]);

  useEffect(() => {
    const lang = searchParams.get('language');
    if (lang) {
      setLanguage(lang);
    } else {
      setLanguage('en');
    }
  }, []);

  useEffect(() => {
    if (!course || !language) {
      return;
    }

    const isLanguageAllowed = availableLanguages.some(
      ({ key }) => key === language
    );

    if (!isLanguageAllowed) {
      setLanguage('en');
      searchParams.delete('language');
      setSearchParams(searchParams);
    }
  }, [course, language, availableLanguages, searchParams, setSearchParams]);

  useEffect(() => {
    let mount = true;
    const fetchViewCourseData = async () => {
      try {
        setIsLoadingData(true);
        const result: IViewCourseDataDTO = await getViewCourseData({
          viewURL,
        });

        if (mount) {
          setCourse(result);
        }
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Forbidden
        ) {
          setShowPasscodeModal(true);
        } else {
          addToast({
            title: 'Error on view course',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
          });
        }
      } finally {
        if (mount) {
          setIsLoadingData(false);
        }
      }
    };

    const handlePostViewCourseData = async () => {
      try {
        const pass = getStorage<string>(EdgeStorageKey.VIEWCOURSE_PASSCODE);
        setIsLoadingData(true);
        const result = await postViewCourseData({
          viewURL,
          passcode: pass ?? '',
        });

        if (mount) {
          setCourse(result);
        }
      } catch (err) {
        const errorCode = (err as IApiThrowsError).response?.data.error.code;
        if (Number(errorCode) >= 500) {
          addToast({
            title: 'Error on view course',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
          });
        } else {
          setShowPasscodeModal(true);
        }
      } finally {
        if (mount) {
          setIsLoadingData(false);
        }
      }
    };

    if (course?.passcodeProtected === true) {
      void handlePostViewCourseData();
    } else {
      void fetchViewCourseData();
    }

    return () => {
      mount = false;
    };
  }, [viewURL]);

  useEffect(() => {
    if (course) {
      void fetchCoursePagesList();

      if (fontsImportString === '') {
        let importString = '';
        course.fonts.forEach((font: IFontDTO) => {
          importString += `<link href="${GOOGLE_FONTS_API_URL}?family=${font.family}" rel="stylesheet">`;
        });

        setFontsImportString(importString);
      }
    }
  }, [course, fontsImportString]);

  useEffect(() => {
    if (
      searchParams.has('language') &&
      searchParams.get('language') !== language
    ) {
      return;
    }

    if (language && sortedPages.length) {
      void fetchCoursePagesData();
    }
  }, [language, sortedPages]);

  useEffect(() => {
    if (pagesData.length) {
      void fetchNextPages();
    }
  }, [pagesData.length]);

  const moveToPreviousPage = useCallback(() => {
    if (isLoadingData) {
      return;
    }
    const newPageIndex = currentPageIndex - 1;
    searchParams.set('page', `${newPageIndex + 1}`);
    if (currentPageIndex + 1 === 1) return;

    setCurrentPageIndex(newPageIndex);
    setShowButtonRight(false);
    setSearchParams(searchParams);

    void fetchCoursePagesData();
  }, [
    currentPageIndex,
    fetchCoursePagesData,
    isLoadingData,
    searchParams,
    setSearchParams,
  ]);

  const moveToNextPage = useCallback(() => {
    if (isLoadingData) {
      return;
    }
    const newPageIndex = currentPageIndex + 1;
    searchParams.set('page', `${newPageIndex + 1}`);
    if (newPageIndex >= sortedPages.length) return;

    setCurrentPageIndex(newPageIndex);
    setShowButtonLeft(false);
    setSearchParams(searchParams);

    void fetchCoursePagesData();
  }, [
    currentPageIndex,
    fetchCoursePagesData,
    searchParams,
    setSearchParams,
    sortedPages.length,
    isLoadingData,
  ]);

  const moveToPage = (index: number) => {
    if (isLoadingData) {
      return;
    }
    searchParams.set('page', `${index + 1}`);
    setCurrentPageIndex(index);
    setSearchParams(searchParams);

    void fetchCoursePagesData();
  };

  const handlerPasscodeSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (loadingPasscodeSubmit) return;
    setLoadingPasscodeSubmit(true);
    setError({});

    postViewCourseData({
      viewURL,
      passcode,
    })
      .then((result: IViewCourseDataDTO) => {
        setCourse(result);
        setShowPasscodeModal(false);
        setLoadingPasscodeSubmit(false);
        setPasscode('');
        setStorage(EdgeStorageKey.VIEWCOURSE_PASSCODE, passcode);
      })
      .catch((error: IApiThrowsError) => {
        setTimeout(() => {
          setLoadingPasscodeSubmit(false);
          const errorMessage = formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          );

          if (
            isAxiosError(error) &&
            error.response?.status === HttpStatusCode.Forbidden
          ) {
            setError({
              passcode: errorMessage,
            });
          } else {
            setError({
              other: errorMessage,
            });
          }
        }, 2000);
      });
  };

  const cssGlobalStyles = useMemo(() => {
    const globalStyle = course?.globalStyle;
    if (!globalStyle || 'length' in globalStyle) return '';

    return generateCssFromGlobalStyle(globalStyle);
  }, [course?.globalStyle]);

  useEffect(() => {
    setShowFullscreenMessageContainer(true);
    const timer = setTimeout(() => {
      setShowFullscreenMessageContainer(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [fullScreen]);

  useEffect(() => {
    setShowPagesContainer(true);
    const timer = setTimeout(() => {
      setShowPagesContainer(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [currentPageIndex]);

  const iframe = desktopIframeRef.current;
  const scrollAmount = 50;

  const handleIframeMessage = useCallback(
    (event: MessageEvent<{ key: string; clientX: number }>) => {
      if (event.origin !== window.location.origin || !iframe) {
        return;
      }

      const iframeDocument =
        iframe.contentDocument ?? iframe.contentWindow?.document;
      const clientWidth = document.documentElement.clientWidth;
      const limitHover = 120;

      if (event.data.key === 'Escape') {
        setFullScreen(false);
      } else if (event.data.key === 'ArrowRight') {
        moveToNextPage();
      } else if (event.data.key === 'ArrowLeft') {
        moveToPreviousPage();
      } else if (event.data.key === 'ArrowUp' && iframeDocument?.defaultView) {
        iframeDocument.defaultView.scrollBy(0, -scrollAmount);
      } else if (
        event.data.key === 'ArrowDown' &&
        iframeDocument?.defaultView
      ) {
        iframeDocument.defaultView.scrollBy(0, scrollAmount);
      }

      if (event.data.clientX < limitHover) {
        setShowButtonLeft(true);
      } else {
        setShowButtonLeft(false);
      }

      if (event.data.clientX > clientWidth - limitHover) {
        setShowButtonRight(true);
      } else {
        setShowButtonRight(false);
      }
    },
    [moveToNextPage, moveToPreviousPage, fullScreen, iframe]
  );

  const keydownFunction = useCallback(
    (event: KeyboardEvent) => {
      if (!iframe) return;

      const iframeDocument =
        iframe.contentDocument ?? iframe.contentWindow?.document;

      if (event.key === 'Escape') {
        setFullScreen(false);
      } else if (event.key === 'ArrowRight') {
        moveToNextPage();
      } else if (event.key === 'ArrowLeft') {
        moveToPreviousPage();
      } else if (event.key === 'ArrowUp' && iframeDocument?.defaultView) {
        iframeDocument.defaultView.scrollBy(0, -scrollAmount);
      } else if (event.key === 'ArrowDown' && iframeDocument?.defaultView) {
        iframeDocument.defaultView.scrollBy(0, scrollAmount);
      }
    },
    [moveToNextPage, moveToPreviousPage, iframe]
  );

  useEffect(() => {
    document.addEventListener('keydown', keydownFunction, false);
    window.addEventListener('message', handleIframeMessage);

    return () => {
      document.removeEventListener('keydown', keydownFunction, false);
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [keydownFunction, handleIframeMessage]);

  useEffect(() => {
    if (desktopIframeRef.current) {
      const iframeDocument = desktopIframeRef.current.contentDocument;

      if (iframeDocument) {
        desktopIframeRef.current.click();
      }
    }
    desktopIframeRef.current?.contentWindow?.focus();
  }, [fullScreen]);

  useEffect(() => {
    if (course != null) {
      if (IS_PROD) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        TagManager.initialize({
          gtmId: GOOGLE_TAG_MANAGER,
          dataLayer: {
            courseId: course.id,
            event: 'Course_View',
          },
        });
      }
    }
  }, [course]);

  const handleChangeLanguage = (value: string) => {
    setPagesData([]);
    setLanguage(value);

    if (value === 'en') {
      searchParams.delete('language');
    } else {
      searchParams.set('language', value);
    }

    setSearchParams(searchParams);
  };

  const desktopIframeSrcDoc = useMemo(
    () =>
      generateIframeDoc({
        fontsImportString,
        cssGlobalStyles,
        currentPage,
        wrapperClass,
        wrapperCss,
        viewport: 'width=device-width, initial-scale=1.0',
      }),
    [
      fontsImportString,
      cssGlobalStyles,
      currentPage?.id,
      currentPage?.cssContent,
      currentPage?.htmlContent,
      currentPage?.hasWrapper,
      wrapperClass,
      wrapperCss,
    ]
  );

  const mobileIframeSrcDoc = useMemo(
    () =>
      generateIframeDoc({
        fontsImportString,
        cssGlobalStyles,
        currentPage,
        wrapperClass,
        wrapperCss,
        viewport:
          'width=375, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
      }),
    [
      fontsImportString,
      cssGlobalStyles,
      currentPage?.id,
      currentPage?.cssContent,
      currentPage?.htmlContent,
      currentPage?.hasWrapper,
      wrapperClass,
      wrapperCss,
    ]
  );

  return (
    <CourseViewerContainer data-cy="courseViewer-container">
      <MainContainer data-cy="page-container">
        <Header data-cy="courseView" $visible={!fullScreen}>
          <HeaderImageContainer
            data-cy="header-imageContainer"
            style={{
              visibility: headerImagePath === '' ? 'hidden' : 'visible',
            }}
          >
            {headerImagePath !== '' && (
              <HeaderImage
                data-cy={`${headerImagePath}`}
                src={`${headerImagePath}`}
              />
            )}
          </HeaderImageContainer>
          <ActionItemsContainer data-cy="action-itemsContainer">
            {!showPasscodeModal && (
              <CustomSelectData
                data={availableLanguages}
                dataCy="select-language"
                name="language"
                onValueChange={handleChangeLanguage}
                value={language}
                zIndex={10}
              />
            )}

            <BaseTooltip
              message={isSplitView ? 'Disable split view' : 'Enable split view'}
              disableHoverableContent
            >
              <ActionIconContainer
                role="button"
                tabIndex={0}
                aria-pressed={isSplitView}
                aria-label={
                  isSplitView ? 'Disable split view' : 'Enable split view'
                }
                data-cy="toggle-split-view"
                onClick={() => {
                  setIsSplitView(!isSplitView);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsSplitView(!isSplitView);
                  }
                }}
              >
                {isSplitView ? (
                  <HiddenMobileLayoutIcon />
                ) : (
                  <VisibleMobileLayoutIcon />
                )}
              </ActionIconContainer>
            </BaseTooltip>

            <BaseTooltip message="Full Screen" disableHoverableContent>
              <ActionIconContainer
                data-cy="select-fullScreen"
                onClick={() => {
                  setFullScreen(true);
                  setPagesMenuOpen(false);
                  setSearchModalOpen(false);
                  setIsSplitView(false);
                }}
              >
                <FullscreenIcon />
              </ActionIconContainer>
            </BaseTooltip>

            {sortedPages.length > 1 && (
              <>
                <BaseTooltip message="Search" disableHoverableContent>
                  <ActionIconContainer
                    data-cy="search-iconContainer"
                    disabled={!allPagesLoaded}
                    onClick={() => {
                      if (!allPagesLoaded) return;
                      setSearchModalOpen(!isSearchModalOpen);
                    }}
                  >
                    <SearchIcon />
                  </ActionIconContainer>
                </BaseTooltip>

                <BaseTooltip message="Pages Menu" disableHoverableContent>
                  <ActionIconContainer
                    data-cy="pagesMenu-container"
                    onClick={() => {
                      setPagesMenuOpen(!isPagesMenuOpen);
                    }}
                  >
                    <PagesMenuIcon />
                  </ActionIconContainer>
                </BaseTooltip>
              </>
            )}
          </ActionItemsContainer>
        </Header>
        {isLoadingData ? (
          <LoadingPagesContainer data-cy="loading-course-viewer-container">
            <LoadingPages dataCy="loading-course-viewer" />
          </LoadingPagesContainer>
        ) : !currentPage ? (
          <NoPagesContainer data-cy="no-pages-founded">
            <NoPagesText data-cy="no-pages-founded-text">
              No pages found
            </NoPagesText>
          </NoPagesContainer>
        ) : (
          <MainContent data-cy="course-viewer-content">
            <PrimaryContent $isSplit={isSplitView}>
              <IframeCustom
                ref={desktopIframeRef}
                srcDoc={desktopIframeSrcDoc}
              />
              <ContainerArrows $visible={fullScreen}>
                {currentPageIndex + 1 !== 1 && (
                  <ToggleLeftButton
                    data-cy="leftArrow-container"
                    onClick={moveToPreviousPage}
                    disabled={currentPageIndex + 1 === 1}
                    $visible={showButtonLeft || !isDesktop}
                  >
                    <LeftArrowIconFullScreen />
                  </ToggleLeftButton>
                )}

                {currentPageIndex + 1 !== sortedPages.length && (
                  <ToggleRightButton
                    data-cy="rightArrow-container"
                    onClick={moveToNextPage}
                    disabled={currentPageIndex + 1 === sortedPages.length}
                    $visible={showButtonRight || !isDesktop}
                  >
                    <RightArrowIconFullScreen />
                  </ToggleRightButton>
                )}

                <BaseTooltip message="Exit Full Screen" disableHoverableContent>
                  <ExitFullScreenMobile $visible={!isDesktop}>
                    <ToggleFullscreenButton
                      data-cy="exit-fullscreen-iconContainer"
                      onClick={() => {
                        setFullScreen(false);
                      }}
                    >
                      <ExitFullscreenIcon />
                    </ToggleFullscreenButton>
                  </ExitFullScreenMobile>
                </BaseTooltip>
              </ContainerArrows>
            </PrimaryContent>

            {isSplitView && (
              <SecondaryContent>
                <MobileIframe
                  ref={mobileIframeRef}
                  srcDoc={mobileIframeSrcDoc}
                />
              </SecondaryContent>
            )}
          </MainContent>
        )}
        <PageContainer
          $visible={(showPagesContainer || !isDesktop) && fullScreen}
        >
          <PageMessage data-cy={currentPageIndex + 1}>
            {currentPageIndex + 1} / {sortedPages.length ?? 1}
          </PageMessage>
        </PageContainer>
        <FullscreenMessageContainer
          $visible={showFullscreenMessageContainer && fullScreen}
        >
          <PageMessage data-cy="fullscreen-exit-message">
            Press <b>ESC</b> to exit full screen mode
          </PageMessage>
        </FullscreenMessageContainer>
        <Footer
          data-cy="footer-courseViewer"
          $visible={!fullScreen && sortedPages.length > 1}
        >
          <LeftArrowIconContainer
            data-cy="leftArrow-container"
            onClick={moveToPreviousPage}
            disabled={currentPageIndex + 1 === 1}
          >
            <LeftArrowIcon />
          </LeftArrowIconContainer>
          <PageMessage data-cy={currentPageIndex + 1}>
            {currentPageIndex + 1} / {sortedPages.length ?? 1}
          </PageMessage>
          <RightArrowIconContainer
            data-cy="rightArrow-container"
            onClick={moveToNextPage}
            disabled={currentPageIndex + 1 === sortedPages.length}
          >
            <RightArrowIcon />
          </RightArrowIconContainer>
        </Footer>
      </MainContainer>

      <OverlayToggleToMobile
        $isShrunken={isPagesMenuOpen}
        onClick={() => {
          setPagesMenuOpen(false);
        }}
      ></OverlayToggleToMobile>
      <AsideContainer
        data-cy="aside-container"
        style={{ display: isPagesMenuOpen ? 'flex' : 'none' }}
      >
        <PageItemsContainer data-cy="pageItems-container">
          {sortedPages.map((coursePage, index) => {
            const { id, title } = coursePage;

            return (
              <PageItem
                data-cy={id}
                key={id}
                disabled={isLoadingData}
                onClick={() => {
                  moveToPage(index);
                  setPagesMenuOpen(false);
                }}
                style={{
                  backgroundColor:
                    currentPageIndex === index
                      ? `${colors.backgroundHover}`
                      : '',
                }}
              >
                <PageIconContainer data-cy="pageIcon-container">
                  <PageIcon />
                </PageIconContainer>
                <PageTitleContainer data-cy="pageTitle-container">
                  <PageTitle data-cy={title}>{title}</PageTitle>
                </PageTitleContainer>
              </PageItem>
            );
          })}
        </PageItemsContainer>
      </AsideContainer>
      <SearchCourseModal
        data-cy="searchCourse-modal"
        isOpen={isSearchModalOpen}
        requestClose={() => {
          setSearchModalOpen(!isSearchModalOpen);
        }}
        coursePages={pagesData ?? []}
        moveToPage={moveToPage}
      />
      <BaseModal
        open={showPasscodeModal}
        hiddenCloseButton
        dataCy="passcode-content-modal"
        loading={loadingPasscodeSubmit}
      >
        <TextContent data-cy="text-container">
          <Title data-cy="title">Course Protected</Title>
          <Description data-cy="description">
            Please enter the Course passcode below:
          </Description>
        </TextContent>
        <Form onSubmit={handlerPasscodeSubmit} data-cy="passcode-form">
          <Fieldset filled={!!passcode} isInvalid={!!error.passcode}>
            <Input
              data-cy="password-input"
              value={passcode}
              id="passcode"
              onChange={(e) => {
                setPasscode(e.target.value.trim());
              }}
              required
              type={showPasscode ? 'text' : 'password'}
            />
            <Label htmlFor="passcode">Passcode</Label>
            <LockButton
              data-cy="toggle-lock-button"
              onClick={() => {
                setShowPasscode(!showPasscode);
              }}
              type="button"
            >
              {showPasscode ? (
                <LockedIcon data-cy="locked-icon" />
              ) : (
                <UnLockedIcon data-cy="unlocked-icon" />
              )}
            </LockButton>
          </Fieldset>
          <ErrorMessage data-cy={error.passcode ?? error.other}>
            {error.passcode ?? error.other}
          </ErrorMessage>
          <SubmitButton
            type="submit"
            data-cy="passcode-submit-button"
            disabled={loadingPasscodeSubmit}
          >
            Submit
          </SubmitButton>
        </Form>
      </BaseModal>
    </CourseViewerContainer>
  );
};
